/**
 * Copies a value to the clipboard.
 *
 * @param value - The value to copy.
 */
export const copyToClipboard = (value: any): void => {
  navigator.clipboard.writeText(value);
};

/**
 * Formats a phone number into a standardized format.
 *
 * @param number - The phone number to format.
 * @returns The formatted phone number.
 */
export const formatPhone = (number: string): string => {
  if (number) {
    // Remove non-numeric characters
    const cleanNumber = number.replace(/\D/g, "");

    // Apply formatting based on length
    if (cleanNumber.length > 10) {
      return cleanNumber.replace(
        /(\d{1})(\d{3})(\d{3})(\d{4})/,
        "+$1($2) $3-$4"
      );
    } else {
      return cleanNumber.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    }
  }
};

/**
 * Cleans a search query by replacing special characters with a placeholder
 * and converting the string to lowercase.
 *
 * @param query - The search query string to be cleaned.
 * @returns The cleaned query string with special characters replaced by `%` and all letters in lowercase.
 *
 * @example
 * ```ts
 * const cleaned = cleanQuery("Hello@World!");
 * console.log(cleaned); // Output: "hello%world%"
 * ```
 */
export const cleanQuery = (query: string): string => {
  return query.replace(/[!"#$%&'()*+,\-/:;<=>?[\]^`{|}~]/gi, "%").toLowerCase();
}

/**
 * Extracts the domain part from a valid email address.
 *
 * @param email - The email address to parse.
 * @returns The domain portion of the email (after the "@") in lowercase,
 *          or `null` if the email is not valid.
 *
 * @example
 * ```ts
 * getEmailDomain("jackson@onlineimage.com"); // returns "onlineimage.com"
 * getEmailDomain("invalid-email"); // returns null
 * ```
 */
export function getEmailDomain(email: string): string | null {
  if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) return null;
  return email.split('@')[1].toLowerCase();
}

export function isJsonValid(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch (e) {
    return false;
  }
}